import { useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ToggleButton, Typography } from '@mui/material';

import ControlledNumericField from 'components/shared/ControlledFields/ControlledNumericField';
import { describePhaseValues } from './ProtocolRuleForm.utils';
import { useFormatPhase } from './hooks/useFormatPhase';
import {
  FieldRow,
  PhaseActions,
  PhaseBox,
  PhaseHeader,
  PhaseIconButton,
  PhaseName,
  PhaseType
} from './ProtocolRuleForm.styles';

import type { ProtocolRuleFormValues } from './ProtocolRuleForm.types';

interface ProtocolRulePhaseProps {
  index: number;
  canRemove: boolean;
  onRemove: () => void;
  /** Open with its fields showing: a new phase, or one that is not complete yet */
  initiallyEditing: boolean;
}

/**
 * The input attributes of a whole-number field of at least 1. ControlledNumericField spreads the
 * caller's `slotProps` over its own, so the step and minimum are repeated next to the accessible name.
 */
const wholeNumberInput = (ariaLabel: string) => ({
  htmlInput: { step: 1, min: 1, 'aria-label': ariaLabel }
});

/**
 * One stretch of weeks with its own type, amount and frequency. Folded it reads like the summary
 * ("week 1 t/m week 4 6× per week") with a pencil and a trash can; the pencil opens a header line
 * with the phase's name, its type as a toggle, a check to fold it again and the trash can, and below
 * it four narrow fields on one row. A phase with a message stays open, so the message is never hidden.
 */
const ProtocolRulePhase = ({
  index,
  canRemove,
  onRemove,
  initiallyEditing
}: ProtocolRulePhaseProps) => {
  'use memo';
  const { t } = useTranslation('common');
  const {
    control,
    formState: { errors }
  } = useFormContext<ProtocolRuleFormValues>();
  const phase = useWatch({ control, name: `phases.${index}` });
  const recurrenceType = phase.recurrenceType;
  const formatPhase = useFormatPhase();
  const [isEditing, setIsEditing] = useState(initiallyEditing);
  const hasMessage = !!errors.phases?.[index];
  const description = describePhaseValues(phase);
  const number = index + 1;
  const phaseName = t('adminPortal.protocols.ruleForm.phase', { number });
  // Each phase repeats the same field labels, so the accessible name says which phase it is
  const ariaLabel = (field: string) =>
    `${phaseName} ${t(
      `adminPortal.protocols.ruleForm.${field}`
    ).toLowerCase()}`;

  const removeButton = canRemove && (
    <PhaseIconButton
      type="trash-can"
      size="small"
      aria-label={t('adminPortal.protocols.ruleForm.removePhase', { number })}
      onClick={onRemove}
    />
  );

  if (!isEditing && !hasMessage) {
    return (
      <PhaseBox data-testid="protocol-rule-phase">
        <PhaseHeader>
          <Typography variant="body2">
            {description ? formatPhase(description) : phaseName}
          </Typography>
          <PhaseActions>
            <PhaseIconButton
              type="pen"
              size="small"
              aria-label={t('adminPortal.protocols.ruleForm.editPhase', {
                number
              })}
              onClick={() => setIsEditing(true)}
            />
            {removeButton}
          </PhaseActions>
        </PhaseHeader>
      </PhaseBox>
    );
  }

  return (
    <PhaseBox data-testid="protocol-rule-phase">
      <PhaseHeader>
        <PhaseName variant="body2" color="text.secondary">
          {phaseName}
        </PhaseName>
        <Controller
          control={control}
          name={`phases.${index}.recurrenceType`}
          render={({ field }) => (
            <PhaseType
              exclusive
              size="small"
              aria-label={`${phaseName} ${t('type').toLowerCase()}`}
              value={field.value}
              // A toggle group can be clicked back to no value; a phase always has a type
              onChange={(_, value) => value && field.onChange(value)}
            >
              <ToggleButton value="WEEKLY">
                {t('adminPortal.protocols.ruleForm.periodic')}
              </ToggleButton>
              <ToggleButton value="TOTAL">
                {t('adminPortal.protocols.ruleForm.total')}
              </ToggleButton>
            </PhaseType>
          )}
        />
        <PhaseActions>
          <PhaseIconButton
            type="check"
            size="small"
            aria-label={t('adminPortal.protocols.ruleForm.closePhase', {
              number
            })}
            onClick={() => setIsEditing(false)}
          />
          {removeButton}
        </PhaseActions>
      </PhaseHeader>
      <FieldRow>
        <ControlledNumericField
          name={`phases.${index}.startWeek`}
          label={t('adminPortal.protocols.ruleForm.startWeek')}
          slotProps={wholeNumberInput(ariaLabel('startWeek'))}
        />
        <ControlledNumericField
          name={`phases.${index}.endWeek`}
          label={t('adminPortal.protocols.ruleForm.endWeek')}
          slotProps={wholeNumberInput(ariaLabel('endWeek'))}
        />
        <ControlledNumericField
          name={`phases.${index}.amount`}
          label={t(
            recurrenceType === 'TOTAL'
              ? 'adminPortal.protocols.ruleForm.totalAmount'
              : 'adminPortal.protocols.ruleForm.amount'
          )}
          slotProps={wholeNumberInput(ariaLabel('amount'))}
        />
        {recurrenceType === 'WEEKLY' && (
          <ControlledNumericField
            name={`phases.${index}.interval`}
            label={t('adminPortal.protocols.ruleForm.interval')}
            slotProps={wholeNumberInput(ariaLabel('interval'))}
          />
        )}
      </FieldRow>
    </PhaseBox>
  );
};

export default ProtocolRulePhase;
